<template>
	<el-row>
		<el-col :span="24">
			<div class="base-form header">
				<h2 class="item" v-if="isCreatingModeOn">
					Новая группа компонентов (узлов)
				</h2>
				<h2 class="item" v-else>
					Карточка группы компонентов <span>{{ serviceComponentGroup?.name }}</span>
				</h2>
			</div>
		</el-col>		
	</el-row>
	<el-row>
		<el-col :span="24">
			<div class="base-form form">
				<el-form :model="ruleForm" ref="jobForm" status-icon :rules="rules">
					<el-form-item label="Название группы" label-position="top" prop="name">
						<el-input v-model="ruleForm.name" />
					</el-form-item>

					<el-form-item label="Родительская группа" label-position="top">						
						<tree-view-component
							url="/api/servicejobcomponentgroups"
							v-model="ruleForm.parentGroupId"							
						/>
					</el-form-item>

					<el-form-item label="Описание группы компонентов (узлов)" label-position="top" prop="description">
						<el-input v-model="ruleForm.description"
							maxlength="255"
							show-word-limit
							type="textarea" />
					</el-form-item>

					<el-form-item>
						<el-row style="width: 100%;">
							<el-col :span="5">
								<el-button type="primary" :disabled="asyncOperationPending" @click="onFormSubmitClick">
									{{ isCreatingModeOn ? 'Создать' : 'Сохранить' }}
								</el-button>
							</el-col>
							<el-col :span="5">
								<el-button @click="onFormCancelClick">
									Отменить
								</el-button>
							</el-col>
							<el-col :span="14">
								<div v-if="!isCreatingModeOn" style="text-align: right;">
									<el-button type="danger" :disabled="asyncOperationPending" @click="onFormDeleteClick">
										Удалить группу
									</el-button>
								</div>
							</el-col>
						</el-row>
					</el-form-item>
				</el-form>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { ElButton, ElTable, ElLink, FormInstance, ElNotification, ElMessageBox } from 'element-plus';
import { reactive } from 'vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import EventHelper from '@/helpers/eventsHelper';
import ServiceJobComponentGroupModel from '@/models/ServiceJobComponentGroupModel';
import JobComponentGroupsApiHelper from '@/helpers/api/JobComponentGroupsApiHelper';
import { noun } from '@/helpers/formatters';
import LinkedLabelValuePair from '@/models/LinkedLabelValuePair';
import TreeViewComponent from '@/components/TreeViewComponent.vue';

interface IServiceJobComponentGroupFormFields {
	id: number;
	name: string | null;
	parentGroupId: number | null;
	description: string | null;	
}

@Options({
	components: {
		ElButton, ElTable, ElLink,
		TreeViewComponent
	},
	props: {
		params: Array
	}
})
export default class ServiceJobComponentGroupView extends Vue {
	/** params */
	params!: Array<any>;

	/** constants */
	isCreatingModeOn = true;
	refreshEvent = 'jobComponentGroupsUpdate';
	componentGroupsTree:  Array<LinkedLabelValuePair<string, number>> | null = null;
	serviceComponentGroup: ServiceJobComponentGroupModel | null = null;
	componentGroupsApiHelper = new JobComponentGroupsApiHelper();
	
	/** view-models */	
	asyncOperationPending = false;
	previoslySelecteTreeNode: number | null = null;
	ruleForm = reactive<IServiceJobComponentGroupFormFields>({
		id: 0,
		name: null,
		parentGroupId: null,
		description: null
	});

	rules = reactive({
		name: [
			{ required: true, message: 'Название группы компонентов не может быть пустым'},
			{ min: 1, max: 255, message: 'Название группы компонентов не может быть короче 1 и длинее 255 символов'},			
		],
		description: [
			{ max: 255, message: 'Описание группы компонентов не может быть длинее 255 символов'},			
		]
	});
	
	mounted() {
		this.initView();
	}

	/**
	 * init form with service-job-component-group's data from incoming params
	 */
	initView() {
		if (!this.params?.length ||
			(this.params.length == 2 && this.params[1] === true)) {
			this.serviceComponentGroup = null;
			this.isCreatingModeOn = true;
			return;
		}

		if (this.params[0]?.id &&
			this.params[0]?.name) {
			this.serviceComponentGroup = new ServiceJobComponentGroupModel(this.params[0]);
			this.ruleForm.id = this.serviceComponentGroup.id;
			this.ruleForm.name = this.serviceComponentGroup.name;
			this.ruleForm.description = this.serviceComponentGroup.description;
			this.ruleForm.parentGroupId = this.serviceComponentGroup.parentGroupId;
			this.isCreatingModeOn = false;
			return;
		}
	}

	async onFormSubmitClick() {
		const form = this.$refs.jobForm as FormInstance;
		form.validate(async (valid) => {
			if (!valid) {
				return
			}
			try {
				this.asyncOperationPending = true;
				if (this.isCreatingModeOn) {
					await this.addComponentGroupAsync();
				} else {
					await this.updateComponentGroupAsync();
				}
				ElNotification({
					message: `Группа '${this.ruleForm.name}' успешно ${this.isCreatingModeOn ? 'создана' : 'обновлена'}`,
					type: 'success',
				});
				EventHelper.dispatchEvent(this.refreshEvent);
				this.closeCurrentTab();
			} catch (e: any) {
				let notificationText;
				if (e.code == 'ERR_NETWORK') {	
					notificationText = `Ошибка соединения, не удалось ${this.isCreatingModeOn ? 'создать' : 'обновить'} группу`;
				} else {
					notificationText = `Ошибка ${this.isCreatingModeOn ? 'создания новой' : 'обновления'} группы`;
				}

				ElNotification({
						message: notificationText,
						type: 'error',
					});
				console.error('Error submitting service-job-component-group', e);
			}
			finally {
				this.asyncOperationPending = false;
			}
				
		});
	}

	onFormCancelClick() {
		this.closeCurrentTab();
	}

	/** deletes an existing service-job-component-group */
	async onFormDeleteClick() {
		const groupId = this.serviceComponentGroup!.id;
		const groupName = this.serviceComponentGroup!.name;
		const relatedJobIdsToGroup = await this.componentGroupsApiHelper.getRelatedJobIdsAsync(store.getters.jwt, groupId);

		let confirmationMessage: string;

		if (!relatedJobIdsToGroup ||
			relatedJobIdsToGroup?.length === 0) {
			confirmationMessage = `Группа сервисных компонентов '${groupName}' будет удалена`;
		} else {
			const conjunctedComponentNoun = noun.conjunct(relatedJobIdsToGroup!.length, 'компонет', 'компонетов', 'компонета');
			confirmationMessage = `В группе '${groupName}' есть ${relatedJobIdsToGroup?.length} ${conjunctedComponentNoun}. При удалении группы все компоненты будут перемещены в группу по-умолчанию`
		}

		ElMessageBox.confirm(
			confirmationMessage,
			{
				confirmButtonText: 'OK',
				cancelButtonText: 'Отмена',
				type: 'error',
			}
		)
		.then(() => this.deleteComponentGroupAsync(groupId));		
	}

	closeCurrentTab() {		
		store.commit('removeTab', store.getters.tab);
	}

	addComponentGroupAsync() {
		const request = {
			name: this.ruleForm.name,
			description: this.ruleForm.description,
			parentGroupId: this.ruleForm.parentGroupId
		};
		const a = axiosHelper.createInstance(store.getters.jwt);
		return a.post('/api/servicejobcomponentgroups', request);
	}

	updateComponentGroupAsync() {
		const request = {
			id: this.ruleForm.id,					
			name: this.ruleForm.name,
			description: this.ruleForm.description,
			parentGroupId: this.ruleForm.parentGroupId
		};
		const a = axiosHelper.createInstance(store.getters.jwt);
		return a.put('/api/servicejobcomponentgroups', request);
	}

	async deleteComponentGroupAsync(groupId: number) {
		try {
			this.asyncOperationPending = true;
			const isDeleted = await this.componentGroupsApiHelper.deleteJobComponentGroupAsync(store.getters.jwt, groupId);
			if (!isDeleted) {
				throw new Error('Unable to delete component group');
			}
			ElNotification({
				title: 'Группа сервисных компонент удалена',
				type: 'success',
			});
			EventHelper.dispatchEvent('jobComponentGroupsUpdate');
			this.closeCurrentTab();
		} catch (e) {
			ElNotification({
				title: 'Ошибка, группа сервисных компонентов не была удалена',
				type: 'error',
			});
		} finally {
			this.asyncOperationPending = false;
		}
	}
}
</script>


<style scoped>
.component-groups-tree-container {
	width: 100%;
}
	.component-groups-tree-container .t-overlay {
		position: absolute;
		z-index: 2;
		opacity: .4;
		background: #fff;
		cursor: default;
		border: var(--el-border);
		border-width: 1px;
		border-color: transparent;
	}

	.component-groups-tree-container .component-groups-scrollbar {
		max-height: 280px; 
		height: 280px; 
		width: 100%; 
		border: var(--el-border);
		border-radius: var(--el-border-radius-base);
	}

		.component-groups-tree-container .component-groups-scrollbar .component-groups-tree {
			width: 100%;
		}
</style>